import React, {useRef, useEffect} from 'react';
import {CloseButton, Modal} from 'react-bootstrap';
import {BaseModalProps} from './types';

const BaseModal: React.FC<BaseModalProps> = ({
    isOpen,
    onClose,
    title,
    size = 'lg',
    centered = true,
    backdrop = 'static',
    backdropClassName,
    className = '',
    children,
    footer,
    hideCloseButton = false,
    hideHeader = false,
    headerActions,
    dialogClassName = '',
    style,
    draggable = false,
}) => {
    const cleanupRef = useRef<(() => void) | null>(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({x: 0, y: 0});

    useEffect(() => {
        if (!draggable || !isOpen) {return;}

        // Use a timeout to ensure the modal is rendered
        const timeout = setTimeout(() => {
            const modalDialog = document.querySelector('.modal-dialog') as HTMLElement;
            if (!modalDialog) {return;}

            // Make modal non-centered and positioned for dragging
            modalDialog.style.position = 'fixed';
            modalDialog.style.margin = '0';
            modalDialog.style.top = '10%';
            modalDialog.style.left = '50%';
            modalDialog.style.transform = 'translateX(-50%)';
            modalDialog.style.zIndex = '1060';

            const handleMouseDown = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                const header = modalDialog.querySelector('.modal-header');
        
                // Only allow dragging from the header
                if (!header?.contains(target)) {return;}
        
                isDragging.current = true;
                const rect = modalDialog.getBoundingClientRect();
                dragOffset.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };
        
                e.preventDefault();
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (!isDragging.current) {return;}
        
                const newX = e.clientX - dragOffset.current.x;
                const newY = e.clientY - dragOffset.current.y;
        
                // Keep modal within viewport bounds
                const maxX = window.innerWidth - modalDialog.offsetWidth;
                const maxY = window.innerHeight - modalDialog.offsetHeight;
        
                const constrainedX = Math.max(0, Math.min(newX, maxX));
                const constrainedY = Math.max(0, Math.min(newY, maxY));
        
                modalDialog.style.left = `${constrainedX}px`;
                modalDialog.style.top = `${constrainedY}px`;
                modalDialog.style.transform = 'none';
            };

            const handleMouseUp = () => {
                isDragging.current = false;
            };

            document.addEventListener('mousedown', handleMouseDown);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            // Store cleanup function
            cleanupRef.current = () => {
                document.removeEventListener('mousedown', handleMouseDown);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }, 100);

        return () => {
            clearTimeout(timeout);
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [draggable, isOpen]);
    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size={size}
            aria-labelledby={title ? 'modal-title' : undefined}
            centered={draggable ? false : centered}
            backdrop={backdrop}
            backdropClassName={backdropClassName}
            className={className}
            dialogClassName={dialogClassName}
            style={style}
        >
            {!hideHeader && (
                <Modal.Header
                    closeButton={!hideCloseButton && !headerActions}
                    className={headerActions ? 'modal-header--with-actions' : undefined}
                >
                    <Modal.Title id="modal-title">
                        {title}
                    </Modal.Title>
                    {headerActions ? (
                        <div className="modal-header__toolbar">
                            <div className="modal-header__actions">
                                {headerActions}
                            </div>
                            {!hideCloseButton ? (
                                <CloseButton onClick={onClose} />
                            ) : null}
                        </div>
                    ) : null}
                </Modal.Header>
            )}
            {!hideHeader ? (
                <Modal.Body>
                    {children}
                </Modal.Body>
            ) : (
                children
            )}
            {footer && (
                <Modal.Footer>
                    {footer}
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default BaseModal;